/**
 * Waits for a WebSocket to open or fail
 * @param websocket - The WebSocket instance to wait for
 * @returns Promise that resolves when connection opens or rejects on error
 */
export async function waitForWebSocketConnection(
  websocket: WebSocket,
): Promise<void> {
  if (websocket.readyState === WebSocket.OPEN) {
    return;
  }

  if (
    websocket.readyState === WebSocket.CLOSED ||
    websocket.readyState === WebSocket.CLOSING
  ) {
    throw new Error('WebSocket is already closed or closing');
  }

  return new Promise<void>((resolve, reject) => {
    function cleanup() {
      websocket.removeEventListener('open', handleOpen);
      websocket.removeEventListener('error', handleError);
      websocket.removeEventListener('close', handleClose);
    }

    function handleOpen() {
      cleanup();
      resolve();
    }

    function handleError(error: Event) {
      // eslint-disable-next-line no-console
      console.error('WebSocket error event:', error);
      const errorMessage =
        error instanceof ErrorEvent
          ? error.message
          : `WebSocket connection failed - ${error.type || 'unknown error'}`;
      cleanup();
      reject(new Error(errorMessage));
    }

    function handleClose(event: CloseEvent) {
      // For "Abnormal Closures" and defined status codes see https://datatracker.ietf.org/doc/html/rfc6455#section-7.2
      // eslint-disable-next-line no-console
      console.warn(
        'WebSocket closed. code:',
        event.code,
        'reason:',
        event.reason,
      );
      cleanup();
      // NOTE: Since we detach this listener on open (in cleanup()), this is an error:
      reject(
        new Error(
          `WebSocket closed before connection established: code: ${event.code}, reason: ${event.reason || 'no reason'}`,
        ),
      );
    }

    websocket.addEventListener('open', handleOpen);
    websocket.addEventListener('error', handleError);
    websocket.addEventListener('close', handleClose);
  });
}
